/**
 * AudioEngine
 *
 * Web Audio API wrapper with three buses: music, ambience, and SFX.
 * Music supports crossfade between tracks. Ambience supports multiple
 * simultaneous layers. SFX are one-shot fire-and-forget.
 *
 * Must call init() after a user interaction (browser autoplay policy).
 */

export type BusName = 'master' | 'music' | 'ambience' | 'sfx';

interface ActiveSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private ambienceGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  // Music: one track at a time with crossfade
  private currentMusic: ActiveSource | null = null;

  // Ambience: multiple layers keyed by ID
  private ambienceLayers = new Map<string, ActiveSource>();

  // Buffer cache to avoid re-fetching
  private bufferCache = new Map<string, AudioBuffer>();

  // Loading promises to avoid duplicate fetches
  private loadingPromises = new Map<string, Promise<AudioBuffer>>();

  get isInitialized(): boolean {
    return this.ctx !== null;
  }

  /**
   * Initialize the AudioContext and wire up bus gain nodes.
   * Must be called from a user gesture (click/tap) to satisfy autoplay policy.
   */
  init(): void {
    if (this.ctx) return;

    this.ctx = new AudioContext();

    // Create bus gain nodes
    this.masterGain = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.ambienceGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();

    // Wire: each bus → master → destination
    this.musicGain.connect(this.masterGain);
    this.ambienceGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.masterGain.connect(this.ctx.destination);
  }

  /**
   * Resume AudioContext if suspended (e.g., after tab becomes inactive).
   */
  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  /**
   * Set volume for a bus (0 to 1).
   */
  setVolume(bus: BusName, volume: number): void {
    const gain = this.getGainNode(bus);
    if (!gain || !this.ctx) return;

    const clamped = Math.max(0, Math.min(1, volume));
    gain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  /**
   * Get current volume for a bus.
   */
  getVolume(bus: BusName): number {
    const gain = this.getGainNode(bus);
    return gain?.gain.value ?? 1;
  }

  // ─── Music Bus ───────────────────────────────────────────────────────

  /**
   * Play a music track, crossfading from current if playing.
   */
  async playMusic(url: string, fadeDuration = 2): Promise<void> {
    if (!this.ctx || !this.musicGain) return;

    const buffer = await this.loadAudio(url);
    if (!buffer) return;

    // Create new source
    const gain = this.ctx.createGain();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    gain.connect(this.musicGain);

    const now = this.ctx.currentTime;

    // Crossfade: fade out old, fade in new
    if (this.currentMusic) {
      const old = this.currentMusic;
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + fadeDuration);
      // Stop old source after fade completes
      setTimeout(() => {
        try { old.source.stop(); } catch { /* already stopped */ }
        old.gain.disconnect();
      }, fadeDuration * 1000 + 100);
    }

    // Fade in new
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + fadeDuration);
    source.start(0);

    this.currentMusic = { source, gain };
  }

  /**
   * Stop current music with optional fade out.
   */
  stopMusic(fadeDuration = 1): void {
    if (!this.ctx || !this.currentMusic) return;

    const { source, gain } = this.currentMusic;
    const now = this.ctx.currentTime;

    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + fadeDuration);

    setTimeout(() => {
      try { source.stop(); } catch { /* already stopped */ }
      gain.disconnect();
    }, fadeDuration * 1000 + 100);

    this.currentMusic = null;
  }

  // ─── Ambience Bus ────────────────────────────────────────────────────

  /**
   * Add an ambience layer. If the layer ID already exists, does nothing.
   */
  async addAmbienceLayer(id: string, url: string, volume = 0.6): Promise<void> {
    if (!this.ctx || !this.ambienceGain) return;
    if (this.ambienceLayers.has(id)) return;

    const buffer = await this.loadAudio(url);
    if (!buffer) return;

    const gain = this.ctx.createGain();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    gain.connect(this.ambienceGain);

    // Fade in
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.5);

    source.start(0);
    this.ambienceLayers.set(id, { source, gain });
  }

  /**
   * Remove an ambience layer with fade out.
   */
  removeAmbienceLayer(id: string, fadeDuration = 0.5): void {
    if (!this.ctx) return;

    const layer = this.ambienceLayers.get(id);
    if (!layer) return;

    const now = this.ctx.currentTime;
    layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
    layer.gain.gain.linearRampToValueAtTime(0, now + fadeDuration);

    setTimeout(() => {
      try { layer.source.stop(); } catch { /* already stopped */ }
      layer.gain.disconnect();
    }, fadeDuration * 1000 + 100);

    this.ambienceLayers.delete(id);
  }

  /**
   * Set volume for a specific ambience layer.
   */
  setAmbienceLayerVolume(id: string, volume: number): void {
    if (!this.ctx) return;
    const layer = this.ambienceLayers.get(id);
    if (!layer) return;

    const clamped = Math.max(0, Math.min(1, volume));
    layer.gain.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.05);
  }

  /**
   * Check if an ambience layer is active.
   */
  hasAmbienceLayer(id: string): boolean {
    return this.ambienceLayers.has(id);
  }

  // ─── SFX Bus ─────────────────────────────────────────────────────────

  /**
   * Play a one-shot sound effect. Fire and forget.
   */
  async playSFX(url: string, volume = 1): Promise<void> {
    if (!this.ctx || !this.sfxGain) return;

    const buffer = await this.loadAudio(url);
    if (!buffer) return;

    const gain = this.ctx.createGain();
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(this.sfxGain);
    gain.gain.value = volume;

    source.start(0);

    // Clean up after playback
    source.onended = () => {
      gain.disconnect();
    };
  }

  // ─── Synthesized Sounds ─────────────────────────────────────────────

  /**
   * Play a synthesized sonar ping. Two-tone descending sweep with decay.
   */
  playSynthPing(volume = 0.35): void {
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // Tone 1: high ping
    const osc1 = this.ctx.createOscillator();
    const gain1 = this.ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1400, now);
    osc1.frequency.exponentialRampToValueAtTime(900, now + 0.15);
    osc1.connect(gain1);
    gain1.connect(this.sfxGain);
    gain1.gain.setValueAtTime(volume, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc1.start(now);
    osc1.stop(now + 0.4);

    // Tone 2: harmonic shimmer (slightly delayed)
    const osc2 = this.ctx.createOscillator();
    const gain2 = this.ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(2100, now + 0.05);
    osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(this.sfxGain);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(volume * 0.5, now + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc2.start(now);
    osc2.stop(now + 0.5);

    // Cleanup
    osc1.onended = () => gain1.disconnect();
    osc2.onended = () => gain2.disconnect();
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────

  /**
   * Stop everything and close the AudioContext.
   */
  destroy(): void {
    this.stopMusic(0);
    for (const id of this.ambienceLayers.keys()) {
      this.removeAmbienceLayer(id, 0);
    }
    this.ctx?.close();
    this.ctx = null;
    this.bufferCache.clear();
    this.loadingPromises.clear();
  }

  // ─── Private helpers ─────────────────────────────────────────────────

  private getGainNode(bus: BusName): GainNode | null {
    switch (bus) {
      case 'master': return this.masterGain;
      case 'music': return this.musicGain;
      case 'ambience': return this.ambienceGain;
      case 'sfx': return this.sfxGain;
    }
  }

  private async loadAudio(url: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;

    // Return cached buffer
    const cached = this.bufferCache.get(url);
    if (cached) return cached;

    // Return in-flight promise if already loading
    const existing = this.loadingPromises.get(url);
    if (existing) return existing;

    // Fetch and decode
    const promise = (async (): Promise<AudioBuffer> => {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.ctx!.decodeAudioData(arrayBuffer);
      this.bufferCache.set(url, audioBuffer);
      this.loadingPromises.delete(url);
      return audioBuffer;
    })();

    this.loadingPromises.set(url, promise);

    try {
      return await promise;
    } catch (err) {
      console.error(`[AudioEngine] Failed to load: ${url}`, err);
      this.loadingPromises.delete(url);
      return null;
    }
  }
}

/** Singleton instance */
export const audioEngine = new AudioEngine();
